import clsx from 'clsx'
import { useState } from 'react'
import { Handle } from 'react-flow-renderer'

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import GetLogo from '../../../components/componentLogos'
import KindColor from '../../../components/kindColors'
import {
  BUFFER_NODE_COLOR,
  INPUT_NODE_COLOR,
  OUTPUT_NODE_COLOR,
  PROCESSOR_NODE_COLOR,
  RESOURCE_NODE_COLOR,
} from '../../../utils/constants'
import styles from './ComponentSummaryEditMode.module.css'
import { GraphNode, NodeAction, NodeData, NodeEvent, NodeHook } from './NodeData'
import { Stack } from '@mui/material'

function getLogo(type: string) {
  const logoName = GetLogo({ type })
  if (logoName === null) {
    return null
  }
  return <img width={'20px'} height={'20px'} style={{ marginTop: '5px' }} alt="logo" src={`/img/logos/${logoName}`} />
}

function title(data: NodeData) {
  if (typeof data.label !== 'undefined') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Stack direction="column">
          <strong style={{ fontSize: '10px', fontWeight: 'bold' }}>{data.label}</strong>
          <small style={{ fontSize: '8px', color: 'grey' }}>{data.type}</small>
        </Stack>
      </div>
    )
  }
  return <small style={{ fontSize: '8px', color: 'grey' }}>{data.type}</small>
}

function capitalize(str: string): string {
  return str.replace(/^\w/, (c) => c.toUpperCase())
}

function shortOperation(op: string, actionKind?: string): JSX.Element | string {
  switch (op) {
    case 'add':
      return (
        <AddBoxOutlinedIcon
          sx={{
            color: `${KindColor({
              kind: actionKind || '',
            })}`,
          }}
        />
      )
    case 'set':
      return (
        <EditOutlinedIcon
          sx={{
            color: `${KindColor({
              kind: actionKind || '',
            })}`,
          }}
        />
      )
    default:
      return capitalize(op)
  }
}

const modalActions: {
  [key: string]: boolean | undefined
} = {
  add: true,
  set: true,
}

function isModalAction(action: NodeAction) {
  return modalActions[action.operation] !== undefined
}

const moveActions: {
  [key: string]: boolean | undefined
} = {
  'move above': true,
  'move below': true,
  'add from': true,
}

function isMoveAction(action: NodeAction) {
  return moveActions[action.operation] !== undefined
}

function sideActionButtons(data: NodeData) {
  if ((data.actions || []).length === 0) {
    return null
  }

  if (data.nodeHooks.isReadOnly()) {
    return null
  }

  const collapsedActions = (data.actions || [])
    .filter((action) => !isMoveAction(action))
    .map((action) => {
      const isModal = isModalAction(action)
      return {
        title: action.operation + ' ' + action.kind,
        hook: isModal ? data.nodeHooks.openActionModal : data.nodeHooks.headlessAction,
        style: data.kind === undefined ? 'bstdioTextBtn' : styles.actionBtn,
        content:
          data.kind === undefined ? (
            shortOperation(action.operation, action.kind)
          ) : (
            <span
              style={{
                borderBottom: `2px solid ${KindColor({
                  kind: action.kind || '',
                })}`,
              }}
            >
              {capitalize(action.operation) + ' ' + action.kind}
            </span>
          ),
        arg: action,
      }
    })

  if (collapsedActions.length === 0) {
    return null
  }

  return (
    <div className={clsx(data.kind === undefined ? styles.titleActions : styles.componentActions)}>
      {collapsedActions.map((action, idx) => {
        return (
          <button
            className={action.style}
            key={idx}
            onClick={(e) => {
              e.stopPropagation()
              action.hook([action.arg])
            }}
            title={action.title}
          >
            {action.content}
          </button>
        )
      })}
    </div>
  )
}

function bigButton(selectedNode: GraphNode | null, data: NodeData) {
  const actions = (data.actions || []).filter((action) => isModalAction(action))
  let title = 'Add a resource'
  let borderColor = RESOURCE_NODE_COLOR
  let optional = true
  if (data.actions && data.actions.length <= 2) {
    optional = false
    switch (data.actions[0].kind) {
      case 'input':
        borderColor = INPUT_NODE_COLOR
        title = 'Add an input'
        break
      case 'buffer':
        borderColor = BUFFER_NODE_COLOR
        title = 'Set a buffer'
        optional = true
        break
      case 'output':
        borderColor = OUTPUT_NODE_COLOR
        title = 'Add an output'
        break
      case 'processor':
        borderColor = PROCESSOR_NODE_COLOR
        title = 'Add a processor'
        optional = true
        break
      case 'metrics':
        title = 'Emit metrics'
        optional = true
        break
      case 'tracer':
        title = 'Emit tracing'
        optional = true
        break
      default:
    }
  }
  return (
    <>
      <button
        className={clsx(styles.bigActionBtn)}
        title={title}
        onClick={(e) => {
          e.stopPropagation()
          data.nodeHooks.openActionModal(actions)
        }}
      >
        <span
          className={styles.bigActionTitle}
          style={{
            borderColor: borderColor,
          }}
        >
          {title}
        </span>
        {optional && (
          <>
            <small>optional</small>
          </>
        )}
      </button>
      {/* TODO: Filter actions that move a resource back into generic resources button */}
      {selectedNode && selectedNode.data ? activatedActionButtons(selectedNode.data, data) : null}
    </>
  )
}

function activatedActionButtons(selectedData: NodeData, data: NodeData) {
  if ((data.actions || []).length === 0) {
    return <></>
  }

  if (data.nodeHooks.isReadOnly()) {
    return <></>
  }

  type presentedAction = {
    title: string
    hook: NodeHook
    arg: NodeAction
  }

  const activatedActions = (data.actions || [])
    .filter((action) => isMoveAction(action))
    .filter((action) => {
      let targetKind = action.kind
      if ((targetKind || '').length === 0) {
        targetKind = data.kind || ''
      }
      return targetKind === selectedData.kind
    })
    .map((action: NodeAction): presentedAction => {
      return {
        title: action.operation,
        hook: data.nodeHooks.headlessAction,
        arg: {
          operation: action.operation,
          path: action.path || data.path,
          value: selectedData.path,
        },
      }
    })
    .filter((action) => {
      if (action.arg.path?.endsWith('_resources')) {
        // No point moving resources back into resources!
        return !action.arg.value?.startsWith(action.arg.path)
      }
      if (!action.arg.value) {
        return true
      }
      return !action.arg.path?.startsWith(action.arg.value)
    })

  return (
    <>
      {activatedActions.map((action, idx) => {
        return (
          <button
            className={clsx(
              styles.activatedActionBtn,
              ((title) => {
                if (title === 'move above') {
                  return styles.activatedActionTopBtn
                }
                if (title === 'move below') {
                  return styles.activatedActionBottomBtn
                }
                if (title === 'add from') {
                  return styles.activatedActionBottomBtn
                }
                return false
              })(action.title)
            )}
            key={idx}
            onClick={(e) => {
              e.stopPropagation()
              action.hook([action.arg])
            }}
            title={'move here'}
          ></button>
        )
      })}
    </>
  )
}

interface ComponentSummaryParams {
  data: NodeData
}

/**
 * Component for displaying a summary of a component or node in the graph.
 *
 * @param {Object} data - The data representing the component or node.
 * @param {string} data.label - The label of the component.
 * @param {string} data.kind - The kind of the component.
 * @param {string} data.path - The path of the component.
 * @param {string} data.type - The type of the component.
 * @param {boolean} data.rootAction - Indicates if the component is a root action.
 * @param {boolean} data.hasChildren - Indicates if the component has children.
 * @param {string} data.hasTarget - The position of the target handle.
 * @param {string} data.hasSource - The position of the source handle.
 * @param {string[]} data.lintErrors - An array of linting errors.
 * @param {boolean} data.isSelected - Indicates if the component is selected.
 * @param {boolean} data.isNotSelected - Indicates if the component is not selected.
 * @param {NodeEvent[]} events - An array of NodeEvent objects representing events associated with the component.
 * @param {boolean} needsAction - Indicates if an action is needed to run the component.
 * @param {function} setNeedsAction - A function to set the needsAction state.
 * @param {NodeEvent[]} events - An array of NodeEvent objects representing events associated with the component.
 * @param {GraphNode | null} selectedNode - The selected node.
 * @param {function} setSelectedNode - A function to set the selected node.
 * @returns {JSX.Element} The JSX element representing the `ComponentSummary` component.
 */
export default function ComponentSummary({ data }: ComponentSummaryParams) {
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  data.setSelectedNode = setSelectedNode

  // RUN STUFF
  const [needsAction, setNeedsAction] = useState(false)
  data.setNeedsAction = setNeedsAction

  const [events, setEvents] = useState<NodeEvent[]>([])
  data.setEvents = setEvents

  if (data.kind === undefined) {
    if (!data.rootAction) {
      return (
        <div className={styles.titleContent}>
          <div className={styles.title}>
            <strong>{data.label}</strong>
          </div>
          {sideActionButtons(data)}
          {selectedNode && selectedNode.data ? activatedActionButtons(selectedNode.data, data) : null}
        </div>
      )
    }
    return bigButton(selectedNode, data)
  }

  const isSelected = selectedNode && selectedNode.data && data.path === selectedNode.data.path
  const isNotSelected = selectedNode && selectedNode.data && data.path !== selectedNode.data.path

  const sideActionBtns = isSelected ? sideActionButtons(data) : null

  const sourceHandleStyle: any = {}
  if (sideActionBtns && data.hasSource === 'bottom') {
    sourceHandleStyle.display = 'none'
  }

  // RUN SUMMARY
  const numConsumed = events.filter((v) => v.type === 'CONSUME').length
  const numDeleted = events.filter((v) => v.type === 'DELETE').length
  const numErrors = events.filter((v) => v.type === 'ERROR').length
  const numProduced = events.filter((v) => v.type === 'PRODUCE').length

  return (
    <>
      {isSelected ? (
        <div className={clsx(styles.componentAura, sideActionBtns && styles.componentAuraWithActions)}>{sideActionBtns}</div>
      ) : null}
      <div className={styles.componentSummary}>
        {data.hasTarget && <Handle isConnectable={false} type="target" position={data.hasTarget} />}
        {events.length > 0 ? (
          <div className={styles.eventSummary}>
            <div title="has events" className={styles.hasEventsAlert}>
              <PlayArrowIcon />
            </div>
            {numConsumed > 0 ? (
              <div title="num consumed" className={styles.eventConsume}>
                {numConsumed}
              </div>
            ) : null}
            {numDeleted > 0 ? (
              <div title="num deleted" className={styles.eventDelete}>
                {numDeleted}
              </div>
            ) : null}
            {numErrors > 0 ? (
              <div title="num errors" className={styles.eventError}>
                {numErrors}
              </div>
            ) : null}
            {numProduced > 0 ? (
              <div title="num produced" className={styles.eventProduce}>
                {numProduced}
              </div>
            ) : null}
          </div>
        ) : null}
        {needsAction ? (
          <div title="action needed to run" className={styles.needsActionAlert}>
            !
          </div>
        ) : null}
        {data.lintErrors ? (
          <div title="linting errors" className={styles.hasLintsAlert}>
            !
          </div>
        ) : null}
        {/* <div className={styles.kindLabel}>{data.kind}</div> */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginLeft: '6px' }}>
          <div
            style={{
              backgroundColor: KindColor({
                kind: data.kind || '',
              }),
              height: '30px',
              width: '30px',
              borderRadius: '5px',
            }}
          >
            {getLogo(data.type || 'none')}
          </div>
          <div
            style={{
              marginLeft: '10px',
            }}
          >
            {title(data)}
          </div>
        </div>
        {data.hasSource && <Handle isConnectable={false} style={sourceHandleStyle} type="source" position={data.hasSource} />}
        {isNotSelected ? activatedActionButtons(selectedNode.data, data) : null}
      </div>
    </>
  )
}
